<template>
  <v-container fluid>
    <v-row>
      <v-col
        cols="12"
        md="9"
        align-self="center"
      >
        <div class="primary--text text-h5 my-2">
          {{ $t('login_account.title') }}
        </div>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="3"
        align-self="center"
        class="pa-1"
      >
        <v-text-field
          v-model="search"
          :loading="loading"
          :disabled="loading"
          outlined
          dense
          :label="$t('common.search')"
          hide-details
          class="elevation-2"
          @change="onPageRefresh(true)"
        />
      </v-col>
    </v-row>
    <v-row v-if="errorState">
      <v-col cols="12">
        <v-alert
          v-model="errorState"
          dense
          type="error"
          close-icon="mdi-close-circle-outline"
          :dismissible="true"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :items="items"
          :headers="headers"
          class="elevation-2 employee-table"
          :loading="loading"
          :sort-by.sync="option.sortBy"
          :sort-desc.sync="option.sortDesc"
          :items-per-page.sync="option.itemsPerPage"
          :page.sync="option.page"
          :server-items-length="totalItems"
          :footer-props="{
            'items-per-page-options': [5, 10, 25, 50],
            'items-per-page-text': $t('login_account.pagination_text')
          }"
          item-key="customer_id"
        >
          <template #[`item.action`]="{item}">
            <v-btn
              outlined
              color="primary white--text"
              @click="onEmployeeSelected(item)"
            >
              {{ $t('group.assign') }}
            </v-btn>
          </template>
          <template #[`item.user_group`]="{item}">
            <div class="primary--text">
              {{ $i18n.locale === 'jp' ? item.user_group.name_jp : (item.user_group.name_en || item.user_group.name_jp) }}
            </div>
          </template>
          <template #footer.page-text>
            <span>{{ option.page }} / {{ totalPages }}</span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog
      v-if="assignUserDialog"
      v-model="assignUserDialog"
      persistent
      :width="$vuetify.breakpoint.lgAndUp ? '60%' : '80%'"
    >
      <v-card>
        <v-card-title class="px-4">
          {{ $t('login_account.assign_user_to_group') }}
        </v-card-title>
        <v-card-text class="px-4">
          <v-form ref="addUser">
            <v-autocomplete
              v-model="groupId"
              :items="groups"
              :item-text="$i18n.locale === 'jp' ? 'name_jp' : 'name_en'"
              item-value="id"
              hide-details="auto"
              outlined
              dense
              clearable
            >
              <template #selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  style="height: auto"
                  class="py-1 px-3"
                  @click="data.select"
                  @click:close="groupId = null"
                >
                  <v-list-item-title>
                    {{ $i18n.locale === 'jp' ? data.item.name_jp : data.item.name_en }}
                  </v-list-item-title>
                </v-chip>
              </template>
            </v-autocomplete>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn
            outlined
            color="primary"
            :disabled="loading"
            :loading="loading"
            @click="onAddUser"
          >
            {{ $t('common.save') }}
          </v-btn>
          <v-btn
            outlined
            color="complimentary"
            :disabled="loading"
            @click="onCloseAssignCustomer"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
        <v-alert
          v-model="formErrorState"
          dense
          type="error"
          dismissible
          class="mb-0 custom-alert-border"
        >
          {{ formError }}
        </v-alert>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>

import QUATSError from '../../../plugins/error'

export default {
  data() {
    return{
      search: '',
      totalItems: 0,
      option: {
        itemsPerPage: 50,
        page: 1,
        sortBy: 'name_kana',
        sortDesc: false,
      },
      totalPages: 1,
      loading: false,
      items: [],
      formError: null,
      error: null,
      formErrorState: false,
      errorState: false,
      assignUserDialog: false,
      groups: [],
      groupId: null,
      selectedCustomerId: null,
    }
  },

  computed: {
    /***
     * @desc validation rule that check for the user group selected data
     * @returns {(function(*): *)[]}
     */
    selectionRule() {
      return [
        v => !!v || this.$t('common.required')
      ]
    },

    /***
     * @desc search params on the table as pagination arguments
     * @returns {string}
     */
    searchParams() {
      let requestParam = `?page_size=${this.option.itemsPerPage}&page=${this.option.page}`
      if (this.search) {
        requestParam += `&search=${this.search}`
      }
      if (this.option.sortBy) {
        requestParam += `&sort_by=${this.option.sortBy}`
      }
      if (this.option.sortDesc) {
        requestParam += `&sort_desc=${this.option.sortDesc}`
      }
      return requestParam
    },

    /***
     * @desc set the data of v-data-table header and translate the header name based on the locale
     * @returns {[{width: string, text: VueI18n.TranslateResult, align: string, value: string, class: string},{width: string, text: VueI18n.TranslateResult, align: string, value: string, class: string},{width: string, text: VueI18n.TranslateResult, sortable: boolean, align: string, value: string, class: string},{width: string, text: VueI18n.TranslateResult, sortable: boolean, align: string, value: string, class: string},{text: string, value: string, class: string}]}
     */
    headers() {
      return [
        { text: this.$t('employee.name'), align: 'start', value: 'name', width: '150px', class: 'primary white--text' },
        { text: this.$t('employee.name_kana'), align: 'start', value: 'name_kana', width: '150px', class: 'primary white--text' },
        { text: this.$t('employee.user_group'), align: 'start', value: 'user_group', sortable: false, width: '150px', class: 'primary white--text' },
        { text: this.$t('employee.permission_group_assignment'), align: 'center', value: 'action', sortable: false, width: '100px', class: 'primary white--text' },
      ]
    }
  },
  watch: {
    option: {
      deep: true,
      handler() {
        this.onPageRefresh(true)
      }
    },
  },
  mounted() {
    this.onPageRefresh(false, true)
  },
  methods: {
    /***
     * @desc listener function when employee selected
     * @param item customer item on the table
     */
    onEmployeeSelected(item) {
      this.formError = null
      this.formErrorState = false
      this.assignUserDialog = true
      this.selectedCustomerId = item.customer_id
      this.groupId = item.user_group_id
    },

    /***
     * @desc function after close assign customer to group
     */
    onCloseAssignCustomer() {
      this.assignUserDialog = false
      this.selectedCustomerId = null
      this.$refs.addUser.reset()
    },

    /***
     * @desc function after add user
     */
    onAddUser() {
      if (this.$refs.addUser.validate()) {
        this.loading = true
        const data = {
          customer_id: this.selectedCustomerId,
        }
        if (this.groupId) {
          data['user_group_id'] = this.groupId
        }
        const promises = [
          window.axios.post('/api/login-account/login-accounts', data)
        ]
        Promise.all(promises)
          .then(() => {
            this.onCloseAssignCustomer()
            window.axios.patch('/api/login-account/login-accounts', { customer_id: data['customer_id']}).then(async () => {
              this.errorState = false
              await this.onPageRefresh()
              this.$store.commit('snackbar/successSnackbar', [this.$t('employee.login_toggle_success')])
            }).catch(err => {
              this.error = new QUATSError(this.$i18n, err).message
              this.errorState = true
              this.loading = false
            })
          }).catch(err => {
            this.formError = new QUATSError(this.$i18n, err).message
            this.formErrorState = true
            this.loading = false
          })
      }
    },

    /***
     * @desc toggle the selected login account
     * @param customer_id ID of the selected account
     */
    // toggleLoginAccount(customer_id) {
    //   const data = {
    //     customer_id: customer_id
    //   }
    //   const promises = [
    //     window.axios.patch('/api/login-account/login-accounts', data)
    //   ]
    //   Promise.all(promises)
    //     .then(() => {
    //       this.errorState = false
    //     }).catch(err => {
    //       this.error = new QUATSError(this.$i18n, err).message
    //       this.errorState = true
    //       this.loading = false
    //     })
    // },

    generateEmployeesId(data) {
      if (data?.data?.length > 0) {
        return {
          customer_ids: data?.data.map(x => x.customer_id)
        }
      }
    },

    /***
     * @desc to get Parent Company information and corporate account information
     * @param search argument to check if it's called after search function
     * @param freshLoad
     * */
    async onPageRefresh(search = false, freshLoad = false) {
      this.loading = true
      try {
        const [employeesResponse, groupsResponse] = await Promise.all([
          window.axios.get(`/api/login-account/employees${this.searchParams}`),
          freshLoad ? window.axios.get('/api/login-account/user-groups') : null
        ])

        const employees = employeesResponse.data?.data?.data || []
        const loginResponse = await window.axios.get('/api/login-account/login-accounts', {
          params: this.generateEmployeesId(employees)
        })
        const logins = loginResponse.data?.data?.data || []

        this.onSearchResult(employees, logins)

        if (freshLoad) {
          const groups = groupsResponse?.data?.data || []
          this.groups = groups.map(x => {
            if (!x.name_en) {
              x.name_en = x.name_jp
            }
            return x
          })
        }
        if (search) {
          this.$store.commit('snackbar/successSnackbar', [this.$t('employee.search_success_message')])
        }
        this.errorState = false
      } catch (error) {
        this.error = new QUATSError(this.$i18n, error).message
        this.errorState = true
      } finally {
        this.loading = false
      }
    },

    /***
     * @desc helper function to generate data after search
     * @param employees all employees list belong to the current account
     * @param loginAccounts list of login account and its data
     */
    onSearchResult(employees, loginAccounts) {
      employees.data.forEach(employee => {
        const login = loginAccounts.find(login => login.customer_id === employee.customer_id)
        login ? employee['user_group_id'] = login.user_group_id : employee['user_group_id'] = null
        login ? employee['is_allow_login'] = !login.deleted_at : employee['is_allow_login'] = false
        login && login.user_group ? employee['user_group'] = login.user_group : employee['user_group'] = {}
      })
      this.items = employees.data
      this.totalPages = employees.last_page
      this.totalItems = employees.total
    },
  }
}
</script>

<style scoped>
::v-deep .employee-table .v-data-table__wrapper .v-data-table-header th.sortable .v-data-table-header__icon {
  color: white !important;
}
.custom-alert-border {
  font-size: 80%;
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border-bottom-right-radius: inherit;
  border-bottom-left-radius: inherit;
}
</style>
